import { useRef, useState, type DragEvent } from 'react';
import { ImagePlus, Trash2, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ImageUploadProps {
  label: string;
  /** Rendered inside the frame when nothing new has been picked. */
  current?: React.ReactNode;
  hint?: string;
  error?: string;
  isUploading?: boolean;
  /** Called with the chosen file — the parent decides where it goes. */
  onSelect: (file: File) => void;
  onRemove?: () => void;
  /** Megabytes. Must match the server's limit or the user finds out late. */
  maxSizeMb?: number;
  accept?: string;
}

export const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/webp,image/svg+xml';

/**
 * The courtesy checks every image picker runs before handing a file over —
 * `null` when it passes, otherwise the message to show. Shared so a picker
 * drawn differently from this one cannot drift from its rules.
 */
export function checkImageFile(
  file: File,
  { accept = ACCEPTED_IMAGE_TYPES, maxSizeMb = 5 }: { accept?: string; maxSizeMb?: number } = {},
): string | null {
  if (!accept.split(',').includes(file.type)) return 'Choose a PNG, JPG, WEBP or SVG image';
  if (file.size > maxSizeMb * 1024 * 1024) return `That image is larger than ${maxSizeMb} MB`;
  return null;
}

/**
 * Picking an image: click or drag, with a preview and client-side size and type
 * checks.
 *
 * The checks here are courtesy, not security — the server validates MIME type,
 * extension and size again, because anything the browser enforces can be
 * skipped entirely.
 */
export function ImageUpload({
  label,
  current,
  hint,
  error,
  isUploading = false,
  onSelect,
  onRemove,
  maxSizeMb = 5,
  accept = ACCEPTED_IMAGE_TYPES,
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  function handleFile(file: File | undefined) {
    if (!file) return;
    const problem = checkImageFile(file, { accept, maxSizeMb });
    setLocalError(problem);
    if (problem) return;

    setPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return URL.createObjectURL(file);
    });
    onSelect(file);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);
    handleFile(event.dataTransfer.files?.[0]);
  }

  const message = error ?? localError;

  return (
    <div className="flex flex-col gap-2.5">
      <span className="text-sm font-extrabold text-slate-700 dark:text-white tracking-tight">{label}</span>

      <div
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={cn(
          'group relative flex items-center gap-3.5 rounded-xl border-2 border-dashed p-3 transition-all duration-200',
          isDragging
            ? 'border-indigo-500 bg-indigo-50/80 dark:bg-indigo-950/40 scale-[1.01] shadow-xl'
            : 'border-indigo-200 dark:border-indigo-800/60 bg-gradient-to-b from-indigo-50/40 via-white to-purple-50/30 dark:from-indigo-950/20 dark:via-[#111c2e] dark:to-purple-950/20 hover:border-indigo-400 hover:shadow-md hover:shadow-indigo-500/10',
          message && 'border-rose-400 bg-rose-50/30',
        )}
      >
        {/* Preview Frame */}
        <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-lg bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 shadow-xs p-1 group-hover:scale-105 transition-transform duration-200">
          {preview ? (
            <img src={preview} alt="" className="h-full w-full object-contain rounded-md" />
          ) : (
            current ?? (
              <div className="grid h-7 w-7 place-items-center rounded-md bg-gradient-to-br from-indigo-500 via-purple-600 to-violet-600 text-white shadow-xs shadow-indigo-500/30">
                <ImagePlus className="h-3.5 w-3.5 text-white drop-shadow-xs" aria-hidden />
              </div>
            )
          )}
        </div>

        {/* Action Controls & Info */}
        <div className="flex flex-col gap-1.5 min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={isUploading}
              onClick={() => inputRef.current?.click()}
              className="rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold px-3 py-1 shadow-xs shadow-indigo-500/25 border border-indigo-400/30 transition-all cursor-pointer inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
            >
              <Upload className="h-3 w-3 shrink-0 text-white" strokeWidth={2.2} />
              <span>{isUploading ? 'Uploading…' : 'Choose image'}</span>
            </button>

            {onRemove && (
              <button
                type="button"
                onClick={() => {
                  setPreview(null);
                  onRemove();
                }}
                className="rounded-full bg-rose-50 dark:bg-rose-950/50 hover:bg-rose-100 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-300 border border-rose-200 dark:border-rose-800/60 px-2.5 py-0.5 text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1"
              >
                <Trash2 className="h-3 w-3" />
                <span>Remove</span>
              </button>
            )}
          </div>

          <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium leading-tight truncate">
            {message ? (
              <span className="text-rose-500 dark:text-rose-400 font-bold">{message}</span>
            ) : (
              (hint ?? `PNG, JPG or WEBP up to ${maxSizeMb} MB.`)
            )}
          </p>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="sr-only"
          onChange={(event) => handleFile(event.target.files?.[0])}
        />
      </div>
    </div>
  );
}
